import React, { useState, useRef } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const PhotoUpload = ({ photos, onChange }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const files = Array.from(e?.target?.files || []);
    processFiles(files);
  };

  const processFiles = (files) => {
    const newPhotos = files?.map((file) => ({
      id: `${Date.now()}-${Math.random()?.toString(36)?.substr(2, 9)}`,
      file,
      preview: URL.createObjectURL(file),
      name: file?.name
    }));
    onChange([...photos, ...newPhotos]);
  };

  const handleDragOver = (e) => {
    e?.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e?.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    setIsDragging(false);
    const files = Array.from(e?.dataTransfer?.files || []);
    const imageFiles = files?.filter(file => file?.type?.startsWith('image/'));
    processFiles(imageFiles);
  };

  const handleRemove = (photoId) => {
    const updatedPhotos = photos?.filter(photo => photo?.id !== photoId);
    onChange(updatedPhotos);
  };

  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    if (e?.dataTransfer) {
      e.dataTransfer.effectAllowed = 'move';
    }
  };

  const handleDragOverPhoto = (e, index) => {
    e?.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    const newPhotos = [...photos];
    const draggedPhoto = newPhotos?.[draggedIndex];
    newPhotos?.splice(draggedIndex, 1);
    newPhotos?.splice(index, 0, draggedPhoto);
    onChange(newPhotos);
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-heading text-2xl font-semibold text-foreground mb-2">
          Upload Photos
        </h2>
        <p className="font-body text-sm text-muted-foreground">
          Add high-quality photos to showcase your trailer (drag to reorder)
        </p>
      </div>

      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`border-2 border-dashed rounded-lg p-8 md:p-12 text-center transition-smooth ${
          isDragging
            ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
        }`}
      >
        <div className="flex flex-col items-center space-y-4">
          <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
            <Icon name="Upload" size={32} color="var(--color-primary)" strokeWidth={2} />
          </div>
          <div>
            <p className="font-body text-base text-foreground mb-1">
              Drag and drop photos here, or
            </p>
            <Button
              variant="outline"
              onClick={() => fileInputRef?.current?.click()}
              iconName="Image"
            >
              Browse Files
            </Button>
          </div>
          <p className="font-caption text-xs text-muted-foreground">
            Supported formats: JPG, PNG, WebP (Max 5MB per file)
          </p>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={handleFileSelect}
          className="hidden"
        />
      </div>

      {photos?.length > 0 && (
        <div>
          <h3 className="font-heading text-lg font-semibold text-foreground mb-4">
            Uploaded Photos ({photos?.length})
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {photos?.map((photo, index) => (
              <div
                key={photo?.id}
                draggable
                onDragStart={(e) => handleDragStart(e, index)}
                onDragOver={(e) => handleDragOverPhoto(e, index)}
                onDragEnd={handleDragEnd}
                className="relative group cursor-move"
              >
                <div className="aspect-square rounded-lg overflow-hidden bg-muted border-2 border-border transition-smooth group-hover:border-primary">
                  <img
                    src={photo?.preview}
                    alt={`Trailer photo ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                </div>
                {index === 0 && (
                  <div className="absolute top-2 left-2 bg-primary text-primary-foreground px-2 py-1 rounded text-xs font-semibold">
                    Primary
                  </div>
                )}
                <button
                  onClick={() => handleRemove(photo?.id)}
                  className="absolute top-2 right-2 w-8 h-8 bg-error text-error-foreground rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-smooth hover:scale-110"
                  aria-label="Remove photo"
                >
                  <Icon name="X" size={16} strokeWidth={2.5} />
                </button>
                <div className="absolute bottom-2 left-2 right-2 bg-background/80 backdrop-blur-sm rounded px-2 py-1 opacity-0 group-hover:opacity-100 transition-smooth">
                  <p className="font-caption text-xs text-foreground truncate">
                    {photo?.name}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="p-4 bg-muted rounded-md flex items-start space-x-3">
        <Icon name="Info" size={20} color="var(--color-primary)" strokeWidth={2} />
        <div className="flex-1">
          <p className="font-body text-sm text-foreground mb-2">
            <span className="font-semibold">Photo Tips:</span>
          </p>
          <ul className="font-body text-sm text-muted-foreground space-y-1 list-disc list-inside">
            <li>Take photos in good lighting conditions</li>
            <li>Show multiple angles and important features</li>
            <li>First photo will be the primary listing image</li>
            <li>Drag photos to reorder them</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default PhotoUpload;